// Inviting members into a 会议室 (src/office/meetinvite.ts). 「邀请」 at the foot of the meeting view's 参会者 list opens the members picker
// (public/members.js: everyone online here who isn't in the room, with their status) with how my last invite to each
// went (已送达 / 稍后 / 接受 / 拒绝 / 超时 / 已进来); tick several and 发送邀请. The invitee's card 「X 邀请你进会议室
// <room>」: 接受 walks their otter into the room (the meeting view opens by itself there), 稍后 folds it into a chip
// that can still be accepted for 10 minutes, 拒绝. In a game 接受 is 「结束后再去」 (members.js whenFree); the office
// sends such an accepted invite again after a reload (status "accepted", defer), so the walk is queued again.
// 「邀请到身边」 (kind "here"): the same invite to one person, from their card (greet.js, anywhere in the office, not
// only in a 会议室): {t:'meetinv', to:[id], here:true}. Their card says 「X 邀请你到 TA 身边」 and 接受 walks their otter
// to where I stand then (members.js walkTo); the card's button shows how it went, and 已到身边 once they're next to me.
import { t, has } from '/i18n.js';
/** How my last invite to someone went: a catalog key per status (public/i18n.js). */
const STATUS = { delivered: 'meeting.invite.st.delivered', later: 'meeting.invite.st.later', accepted: 'meeting.invite.st.accepted', declined: 'meeting.invite.st.declined',
  expired: 'meeting.invite.st.expired', left: 'meeting.invite.st.left', joined: 'meeting.invite.st.joined' };
const status = (s) => (STATUS[s] ? t(STATUS[s]) : s);
const CLS = { accepted: 'ok', joined: 'ok', declined: 'off', expired: 'off', left: 'off', later: 'wait' };
const HERE_ERR = { E_HERE: 'meeting.invite.here.err.E_HERE' }; // a here-invite's own wording, over errText's
/** As the office's ARRIVE_TILES: this close (tiles), in the same room, is 「身边」 already, no invite to send. */
const ARRIVE_TILES = 2.5;
/** The words for the office's refusal codes (src/office/meetinvite.ts): meeting.invite.err.<code>; null for one this page doesn't know. */
const errText = (c, here) => (here && HERE_ERR[c] ? t(HERE_ERR[c]) : c && has(`meeting.invite.err.${c}`) ? t(`meeting.invite.err.${c}`) : null);

/**
 * @param o {send, toast, MAP, room: () => zone id | null (the 会议室 I'm in), members: public/members.js, zoneAt, me, peers}
 * @returns {{open(): void, forCard(p): ({icon, label, disabled?, title?, go?} | null), msg(m): void}}
 */
export function initMeetInvite(o) {
  const mine = new Map(); // my invites: id -> {id, kind, to, toName, toKind, status, defer?, expires, seen (when its status came)}
  const roomName = () => o.MAP.zones.find((z) => z.id === o.room())?.name ?? '';
  // by connection id, or (after they reconnect) by their login / principal: never by name, which two people can share
  // (kind: "room" or "here"; an invite without one is a room invite)
  const lastTo = (p, kind = 'room') => [...mine.values()].filter((i) => (i.kind ?? 'room') === kind && (i.to === p.id || (i.toKey && i.toKey === (p.gid ?? p.sub)))).at(-1);
  /** Past `expires` with no word (the office may have missed telling me): 超时. */
  function lapse() { const now = Date.now(); for (const i of mine.values()) if ((i.status === 'delivered' || i.status === 'later') && i.expires && i.expires <= now) i.status = 'expired'; }

  function open() {
    if (!o.room()) { o.toast(t('meeting.invite.err.E_NOT_THERE')); return; }
    o.members.pick({
      title: () => t('meeting.invite.title', { room: roomName() }), icon: 'door', max: 8 /* the office's BATCH */, alive: () => !!o.room(),
      top: o.link && !o.guest ? () => o.link.top('room') : undefined, // 复制参会链接 (public/meetlink.js)
      emptyText: t('meeting.invite.empty'),
      sendLabel: t('meeting.invite.send'), hint: t('meeting.invite.hint', { max: 8 }),
      exclude: (p) => o.zoneAt(p.x, p.y)?.id === o.room(),
      state: (p) => {
        lapse();
        const last = lastTo(p, 'room'); if (!last) return null;
        return { text: last.status === 'accepted' && last.defer ? t('meeting.invite.st.acceptedLater') : status(last.status), cls: CLS[last.status] ?? '', lock: last.status === 'delivered' || last.status === 'later' };
      },
      onSend: (ids) => o.send({ t: 'meetinv', to: ids }),
    });
  }

  /** 邀请到身边 on p's card (greet.js): how my last one to them went, or null if they're next to me already. */
  function forCard(p) {
    const me = o.me(); if (!me || p.id === me.id) return null;
    lapse();
    const last = lastTo(p, 'here'), s = last?.status;
    if (s === 'delivered' || s === 'later') return { icon: 'flag', label: t('meeting.invite.here.status', { status: status(s) }), disabled: true, title: t('meeting.invite.here.waiting') };
    // on the way: until they arrive (or a minute, should they stop on the way: then it can be sent again)
    if (s === 'accepted' && Date.now() - (last.seen ?? 0) < 60_000) return { icon: 'check', label: t(last.defer ? 'meeting.invite.st.acceptedLater' : 'meeting.invite.st.acceptedComing'), disabled: true };
    // as the office's nextTo(): close, and in the same room (someone behind a 会议室 wall isn't 身边)
    if (Math.hypot(p.x - me.x, p.y - me.y) <= ARRIVE_TILES && (o.zoneAt(p.x, p.y)?.id ?? null) === (o.zoneAt(me.x, me.y)?.id ?? null)) return s === 'joined' ? { icon: 'check', label: t('meeting.invite.here.arrived'), disabled: true } : null;
    return { icon: 'flag', label: s && s !== 'accepted' && s !== 'joined' ? t('meeting.invite.here.status', { status: status(s) }) : t('meeting.invite.here.label'),
      title: t('meeting.invite.here.tip'), go: () => o.send({ t: 'meetinv', to: [p.id], here: true }) };
  }

  /** An invite to my side (kind "here"): its card; 接受 walks me to the sender, wherever they are by then. */
  function hereCard(m) {
    const key = 'm:' + m.id, who = m.name || t('meeting.otter');
    // by connection id, or (they reconnected) by their login: never by name
    const sender = () => [...o.peers().values()].find((p) => p.id === m.from || (m.fromKey && (p.gid ?? p.sub ?? p.id) === m.fromKey));
    const go = () => { const p = sender(); if (p) o.members.walkTo(p, who); else o.toast(t('meeting.invite.here.gone', { name: who })); };
    const later = (until) => o.members.whenFree(go, key, until);
    if (m.status === 'accepted') { later(m.expires); return; } // 「结束后再去」 from before a reload: still on
    const answer = (a, defer) => o.send({ t: 'meetinv_answer', id: m.id, answer: a, ...(defer && { defer: true }) });
    o.members.addCard({
      id: key, icon: 'flag', defer: true, text: t('meeting.invite.here.card', { name: who }), chip: t('meeting.invite.here.chip', { name: who }),
      quiet: !!m.quiet, expires: m.expires, folded: m.status === 'later',
      accept: (defer) => {
        answer('accept', defer);
        if (defer) { o.toast(t('meeting.invite.here.deferred', { name: who }), null, 'flag'); later(Date.now() + 30 * 60_000); }
        else go();
      },
      later: () => answer('later'), laterMs: 10 * 60_000,
      decline: () => answer('decline'),
    });
  }

  // room invites to me still good (id -> {room, until}): a locked 会议室 lets me in with one (public/meethost.js mayEnter,
  // as the office's meetinv.invitedTo); accepted ones count until the office's arrival window (30 min)
  const toMe = new Map();
  /** Do I hold an invite into room `id` (open or accepted, not over)? 「邀请到身边」 from someone standing in it counts too. */
  function invitedTo(id) {
    const now = Date.now();
    for (const [k, i] of toMe) { if (i.until <= now) toMe.delete(k); else if (i.room === id || (i.from && o.zoneAt(i.from.x, i.from.y)?.id === id)) return true; }
    return false;
  }

  return {
    open, forCard, invitedTo,
    msg(m) {
      if (m.t === 'meetinv') toMe.set(m.id, { room: m.kind === 'here' ? null : m.room, until: m.status === 'accepted' ? m.expires : Math.max(m.expires || 0, Date.now() + 30 * 60_000),
        // the sender by connection id, or (they reconnected) by login, as hereCard's sender(): never by name
        get from() { return m.kind === 'here' ? [...o.peers().values()].find((p) => p.id === m.from || (m.fromKey && (p.gid ?? p.sub ?? p.id) === m.fromKey)) : null; } });
      else if (m.t === 'meetinv_gone' && m.status !== 'accepted') toMe.delete(m.id);
      if (m.t === 'meetinv' && m.kind === 'here') hereCard(m);
      else if (m.t === 'meetinv') { // an invite to me (or one still open, again after a reload)
        const room = m.room, name = m.roomName || m.room, key = 'm:' + m.id;
        // the walk waits up to the office's arrival window (ARRIVE_MS, 30 min): after a reload m.expires is that deadline
        const later = (until) => o.members.whenFree(() => o.members.walkToZone(room, name), key, until);
        if (m.status === 'accepted') { later(m.expires); return; } // 「结束后再去」 from before a reload: still on
        const answer = (a, defer) => o.send({ t: 'meetinv_answer', id: m.id, answer: a, ...(defer && { defer: true }) });
        o.members.addCard({
          id: key, icon: 'door', defer: true, text: t('meeting.invite.card', { name: m.name || t('meeting.otter'), room: name }), chip: t('meeting.invite.chip', { name: m.name || t('meeting.otter'), room: name }),
          quiet: !!m.quiet, expires: m.expires, folded: m.status === 'later',
          accept: (defer) => {
            answer('accept', defer);
            if (defer) { o.toast(t('meeting.invite.deferred', { room: name }), null, 'door'); later(Date.now() + 30 * 60_000); }
            else o.members.walkToZone(room, name);
          },
          later: () => answer('later'), laterMs: 10 * 60_000, // the office keeps it 10 more minutes (LATER_MS)
          decline: () => answer('decline'),
        });
      } else if (m.t === 'meetinv_gone') { // it's over (or accepted, on this or another tab): the card goes
        o.members.dropCard('m:' + m.id);
        if (m.status !== 'accepted') o.members.unwait('m:' + m.id); // a queued 「结束后再去」 walk stays until they arrive (joined)
      }
      else if (m.t === 'meetinv_ok') {
        const here = m.kind === 'here', err = (c) => errText(c, here);
        for (const i of m.sent ?? []) mine.set(i.id, { ...i, seen: Date.now() });
        const names = (m.sent ?? []).map((i) => i.toName), bad = m.refused ?? [];
        if (names.length) o.toast(t(here ? 'meeting.invite.sentHere' : 'meeting.invite.sent', { names: names.join(t('meeting.sep')), bad: bad.length }), null, here ? 'flag' : 'door');
        else if (bad.length) {
          const one = err(bad[0].code) ?? t('meeting.invite.failed');
          o.toast(bad.length === 1 || !bad[0].toName ? (bad[0].toName ? t('meeting.invite.failedFor', { name: bad[0].toName, why: one }) : one)
            : t('meeting.invite.failedAll', { list: bad.map((r) => t('meeting.invite.failedItem', { name: r.toName ?? '?', why: err(r.code) ?? '' })).join(t('meeting.invite.failedSep')) }));
        }
        while (mine.size > 40) mine.delete(mine.keys().next().value);
        o.members.refresh();
      } else if (m.t === 'meetinv_status') {
        const was = mine.get(m.id); mine.set(m.id, { ...was, ...m, seen: was?.status === m.status ? was.seen : Date.now() });
        const n = m.toName, here = m.kind === 'here';
        const say = { accepted: m.defer ? 'meeting.invite.said.acceptedLater' : 'meeting.invite.said.accepted', declined: 'meeting.invite.said.declined', later: 'meeting.invite.said.later',
          expired: 'meeting.invite.said.expired', joined: here ? 'meeting.invite.said.arrived' : 'meeting.invite.said.joined', left: 'meeting.invite.said.left' }[m.status];
        if (say && was?.status !== m.status) o.toast(t(say, { name: n }), null, m.status === 'joined' || m.status === 'accepted' ? 'check' : here ? 'flag' : 'door');
        o.members.refresh();
      }
    },
  };
}
